import { useCallback } from "react";
import {
  usePostHog,
  useFeatureFlagEnabled,
  useFeatureFlagPayload as usePostHogFeatureFlagPayload,
} from "@posthog/react";

// Single seam over the feature-flag provider. Flags are created, toggled, and
// rolled out in the PostHog UI (source of truth); code only reads them by key.
// Keeping the SDK import here (and in src/lib/feature-flags/) means swapping
// providers later only touches these files.

/** Returns whether a feature flag is enabled. `false` until flags load. */
export function useFeatureFlag(key: string): boolean {
  return useFeatureFlagEnabled(key) ?? false;
}

/** Returns a flag's payload (for multivariate / JSON-payload flags), if any. */
export function useFeatureFlagPayload(key: string): unknown {
  return usePostHogFeatureFlagPayload(key);
}

/**
 * Identity for feature-flag targeting. Identifying a user lets flags target them
 * directly (by distinct id) or by the properties passed here (e.g. roll a flag
 * out to everyone in one label). Keep properties minimal — only what we target
 * on — and avoid PII/customer names: prefer ids over human-readable values.
 *
 * `identify` should run once the user is known (login / session restore) and
 * `reset` on logout so a shared browser doesn't carry one user's identity into
 * the next session.
 */
export function useFeatureFlagIdentity() {
  const posthog = usePostHog();

  const identify = useCallback(
    (distinctId: string, properties?: Record<string, unknown>) => {
      posthog?.identify(distinctId, properties);
    },
    [posthog],
  );

  const reset = useCallback(() => {
    posthog?.reset();
  }, [posthog]);

  return { identify, reset };
}
